import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react'
import Item from './item'
import FloorTitle from 'Components/floor/floor-title'
import { ICommonVideoData } from '../../services/home'
import { useResponsive } from 'Components/hooks/use-responsive'
import { Swiper, SwiperSlide } from 'swiper/react'
import { logger, metrics } from 'lib/slardar-utils'

interface IProps {
  list: any[]
  onTitleClick: () => void
  onNavItemClick: (item: any) => void
  titleIcon?: { label: string; link?: string }
  titleObj?: { link: string; label: string }
  navList?: { link: string; label: string; icon?: string; className?: string }[]
  contentWrapClassName?: string
  sliderWrapClassName?: string
  renderSlider: (
    groups: any,
    activeIndex: number,
    breakPoint?: any
  ) => React.ReactNode
  beforeRenderSlider?: React.ReactNode
  afterRenderSlider?: React.ReactNode
  narrowScreenItemSize?: number
  wideScreenItemSize?: number
  more?: string
  swiperConfig?: any
}

const CommonVideoCard: React.FC<IProps> = ({
  list,
  titleIcon,
  titleObj,
  navList,
  renderSlider,
  contentWrapClassName = 'tw-grid tw-grid-cols-40 tw-gap-x-12',
  sliderWrapClassName = 'tw-col-span-40',
  beforeRenderSlider = null,
  afterRenderSlider = null,
  narrowScreenItemSize = 10,
  wideScreenItemSize = 8,
  swiperConfig,
  more = '',
  onTitleClick,
  onNavItemClick,
}) => {
  const NARROW_SCREEN_ITEM_SIZE = narrowScreenItemSize
  const WIDE_SCREEN_ITEM_SIZE = wideScreenItemSize
  const [noop, setRefresh] = useState<number>()
  const [swiper, setSwiper] = useState<any>(null!)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [breakPoint, setBreakPoint] = useState<{
    slidesPerColumn: number
    slidesPerView: number
  }>(null!)
  const responsive = useResponsive()
  const navigationPrevRef = React.useRef(null)
  const navigationNextRef = React.useRef(null)

  const handleSlide = useCallback(
    (action: 'prev' | 'next') => {
      if (action === 'prev') {
        if (swiper) {
          swiper.slidePrev()
        }
      }
      if (action === 'next') {
        if (swiper) {
          swiper.slideNext()
        }
      }
      setRefresh(Math.random())
    },
    [swiper, setRefresh]
  )

  const groups: any = useMemo(() => {
    const result: any[][] = []
    const isWideScreen = responsive?.['2xl'] ?? true
    const itemSize = isWideScreen
      ? WIDE_SCREEN_ITEM_SIZE
      : NARROW_SCREEN_ITEM_SIZE

    list.forEach((item, index) => {
      const gIndex = Math.floor(index / itemSize)
      if (!result[gIndex]) {
        result[gIndex] = []
      }
      result[gIndex].push(item)
    })

    return result
  }, [
    responsive?.['2xl'],
    list,
    WIDE_SCREEN_ITEM_SIZE,
    NARROW_SCREEN_ITEM_SIZE,
  ])

  const handleSlideChange = useCallback(
    (swiper: any) => {
      setActiveIndex(swiper.snapIndex)
      swiperConfig?.onSlideChange && swiperConfig.onSlideChange(swiper)
    },
    [setActiveIndex, swiperConfig]
  )

  const handleBreakPoint = useCallback(
    (swiper: any, breakPoints: any) => {
      setBreakPoint({
        slidesPerColumn: breakPoints?.slidesPerColumn,
        slidesPerView: breakPoints?.slidesPerView,
      })
    },
    [swiperConfig]
  )

  const titleIconUrl = titleIcon?.label

  return (
    <div className="floor-card fix-ssr-swiper">
      <FloorTitle
        titleElement={
          titleIcon && (
            <a href={titleIcon?.link} target={'_blank'} className="title" />
          )
        }
        className={`floor-title`}
        titleObj={titleObj}
        navList={navList}
        onNavItemClick={onNavItemClick}
        onTitleClick={onTitleClick}
        more={more}
        slider={{
          cur: (swiper?.activeIndex || 0) + 1,
          total: groups.length,
          onSlide: handleSlide,
        }}
        ref={{
          prevRef: navigationPrevRef,
          nextRef: navigationNextRef,
        }}
      />
      <div className={`fix-mt ${contentWrapClassName}`}>
        {beforeRenderSlider}
        <div className={sliderWrapClassName}>
          <div style={{ width: '100%', overflow: 'hidden' }}>
            <Swiper
              navigation={{
                nextEl: navigationNextRef.current,
                prevEl: navigationPrevRef.current,
              }}
              speed={450}
              observeParents={true}
              observer={true}
              resizeObserver={true}
              spaceBetween={12}
              watchOverflow={true}
              slidesPerView={1}
              slidesPerGroup={1}
              preventClicks={false}
              onSwiper={setSwiper}
              simulateTouch={false}
              onBreakpoint={handleBreakPoint}
              updateOnWindowResize={true}
              {...(swiperConfig || {})}
              onSlideChange={handleSlideChange}
            >
              {renderSlider(groups, activeIndex, breakPoint)}
            </Swiper>
          </div>
        </div>
        {afterRenderSlider}
      </div>
      <style jsx>
        {`
          .floor-card {
          }
          :global(.floor-card .swiper-container) {
            width: 100%;
          }
          :global(.floor-card .floor-title) {
            align-items: center;
          }
          .title {
            display: block;
            width: 117px;
            height: 30px;
            border-radius: 2px;
            background: url(${titleIconUrl}) no-repeat center center;
            background-size: cover;
          }
          :global(.common-video-card) {
            display: flex;
          }
          :global(.fix-swiper-mutil-row-mt-16 .fix-mt) {
            margin-top: -9px;
          }
          :global(.fix-mt) {
            margin-top: 12px;
          }
        `}
      </style>
    </div>
  )
}

export default CommonVideoCard
